const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (res.status === 401) {
    window.location.href = '/auth/login?returnTo=' + encodeURIComponent(window.location.pathname);
    throw new Error('Unauthorised');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () =>
    fetch('/auth/me', { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<{ id: string; email: string; name: string }>) : null))
      .catch(() => null),

  runCheck: (body: { domain: string; selector?: string; probeCommonDkim?: boolean }) =>
    request<DomainCheckResult>('/checks', { method: 'POST', body: JSON.stringify(body) }),

  recentChecks: () => request<{ checks: { id: string; domain: string; ran_at: string }[] }>('/checks/recent'),

  dkimProbe: (body: { domain: string; selector?: string }) =>
    request<{ domain: string; results: DkimSelectorResult[] }>('/checks/dkim', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  reportSummary: (q: ReportQuery) =>
    request<{ totals: Totals }>(`/reports/summary${qs(q)}`),
  reportVolume: (q: ReportQuery) =>
    request<{ days: { day: string; volume: string; aligned: string; not_aligned: string }[] }>(
      `/reports/volume${qs(q)}`,
    ),
  reportSources: (q: ReportQuery) =>
    request<{ sources: SourceRow[] }>(`/reports/sources${qs(q)}`),
  reportTopFailing: (q: ReportQuery) =>
    request<{ rows: { ip: string; header_from: string | null; organisation: string | null; failing: string }[] }>(
      `/reports/top-failing${qs(q)}`,
    ),
  reportByProvider: (q: ReportQuery) =>
    request<{ providers: { provider: string; report_count: number; volume: string }[] }>(
      `/reports/by-provider${qs(q)}`,
    ),
  reportDomains: () => request<{ domains: DomainSummary[] }>('/reports/domains'),
  reportList: (q: ReportQuery & { failingOnly?: boolean }) =>
    request<{ reports: ReportRow[] }>(`/reports/reports${qs(q)}`),
  reportDetail: (id: string) =>
    request<{ report: ReportRow & { policy_p?: string }; records: ReportRecord[] }>(
      `/reports/reports/${encodeURIComponent(id)}`,
    ),
  source: (ip: string) =>
    request<{ ip: string; rows: SourceDrillRow[] }>(`/reports/source/${encodeURIComponent(ip)}`),

  forensicList: (q: ReportQuery) =>
    request<{ rows: ForensicRow[] }>(`/reports/forensic${qs(q)}`),
  forensicDetail: (id: string) =>
    request<{ report: ForensicDetail }>(`/reports/forensic/${encodeURIComponent(id)}`),

  ingestionRuns: () => request<{ runs: IngestionRun[] }>('/ingestion/runs'),
  ingestionRun: () =>
    request<{ runId: string; seen: number; imported: number; skipped: number; errors: string[] }>(
      '/ingestion/run',
      { method: 'POST' },
    ),

  adminConfig: () =>
    request<{
      allowedEmails: string[];
      allowedGroupIds: string[];
      graph: {
        mode: string;
        mailboxUser?: string;
        inboxFolder: string;
        processedFolder: string;
        intervalSeconds: number;
      };
    }>('/admin/config'),
  adminDomains: () => request<{ domains: { id: string; name: string; notes: string | null }[] }>('/admin/domains'),
  adminAddDomain: (body: { name: string; notes?: string }) =>
    request<{ domain: { id: string; name: string } }>('/admin/domains', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminDeleteDomain: (id: string) =>
    request<{ ok: true }>(`/admin/domains/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

function qs(q: ReportQuery & { failingOnly?: boolean }): string {
  const sp = new URLSearchParams();
  if (q.domain) sp.set('domain', q.domain);
  if (q.from) sp.set('from', q.from);
  if (q.to) sp.set('to', q.to);
  if (q.failingOnly) sp.set('failingOnly', 'true');
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface ReportQuery {
  domain?: string;
  from?: string;
  to?: string;
}

export interface Finding {
  severity: 'fail' | 'warning' | 'pass' | 'info';
  code: string;
  message: string;
  fix?: string;
}

export interface DmarcRecommendation {
  recommended: string;
  rationale: string[];
  stage: 'starter' | 'rollout' | 'enforce' | 'reject' | 'harden';
  alreadyStrong: boolean;
}

export interface CheckResult {
  status: 'pass' | 'warning' | 'fail' | 'missing' | 'unknown' | 'info';
  raw: string[];
  parsed?: unknown;
  findings: Finding[];
  summary: string;
  score?: number;
  totalLookups?: number;
  meta?: Record<string, unknown>;
  recommendation?: DmarcRecommendation;
}

export interface DkimSelectorResult extends CheckResult {
  selector: string;
}

export interface DomainCheckResult {
  domain: string;
  ranAt: string;
  dmarc: CheckResult;
  spf: CheckResult;
  dkim?: DkimSelectorResult;
  dkimSelectors?: DkimSelectorResult[];
  mx: CheckResult;
  bimi: CheckResult;
  mtaSts: CheckResult;
  tlsRpt: CheckResult;
}

export interface Totals {
  total_volume: string;
  pass_dmarc: string;
  fail_dmarc: string;
  pass_spf: string;
  pass_dkim: string;
}
export interface SourceRow {
  ip: string;
  organisation: string | null;
  ptr: string | null;
  volume: string;
  aligned: string;
  failing: string;
}
export interface DomainSummary {
  domain: string;
  report_count: number;
  last_import: string;
  volume: string;
}
export interface ReportRow {
  id: string;
  report_id: string;
  org_name: string | null;
  org_email: string | null;
  domain: string;
  date_begin: string;
  date_end: string;
  policy_p?: string | null;
  policy_pct?: number | null;
  imported_at?: string;
  volume?: string;
  pass_volume?: string;
  fail_volume?: string;
  spf_fail_volume?: string;
  dkim_fail_volume?: string;
}
export interface ReportRecord {
  id: string;
  source_ip: string;
  count: number;
  disposition: string | null;
  dkim_aligned: boolean | null;
  spf_aligned: boolean | null;
  envelope_to: string | null;
  envelope_from: string | null;
  header_from: string | null;
  dkim_results: unknown;
  spf_results: unknown;
  reason: unknown;
}
export interface SourceDrillRow {
  domain: string;
  org_name: string | null;
  date_begin: string;
  count: number;
  disposition: string | null;
  dkim_aligned: boolean | null;
  spf_aligned: boolean | null;
  header_from: string | null;
}
export interface ForensicRow {
  id: string;
  imported_at: string;
  source_ip: string | null;
  reported_domain: string | null;
  arrival_date: string | null;
  auth_failure: string | null;
  original_mail_from: string | null;
  original_rcpt_to: string | null;
  identity_alignment: string | null;
  reporting_mta: string | null;
  user_agent: string | null;
  subject: string | null;
  failing_message_id: string | null;
  dkim_domain: string | null;
  dkim_selector: string | null;
  authentication_results: string | null;
}

export interface ForensicDetail extends ForensicRow {
  feedback_type: string | null;
  delivery_result: string | null;
  spf_dns: string | null;
  dkim_identity: string | null;
  raw_headers: Record<string, string>;
}

export interface IngestionRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  messages_seen: number;
  messages_imported: number;
  messages_skipped: number;
  error: string | null;
}
